import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Drawer } from '../../components/Drawer/Drawer';
import { useToast } from '../../components/Toast';
import { cn } from '../../lib/cn';
import { errorMessage } from '../../lib/errorMessage';
import { formatILS, INVOICE_STATUS_LABELS_HE, type ProjectInvoice } from './projectFinance';
import { useUpsertInvoice } from './projectFinanceQueries';
import styles from './projectFinance.module.css';

const FORM_ID = 'invoice-form';

const schema = z.object({
  invoice_number: z.string().trim().min(1, 'שדה חובה').max(60, 'עד 60 תווים'),
  amount: z.number({ invalid_type_error: 'הזן סכום' }).positive('הסכום חייב להיות גדול מאפס'),
  sent_at: z.string(),
  cancelled: z.boolean(),
  notes: z.string().max(2000, 'עד 2000 תווים'),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  invoice: ProjectInvoice | null;
  projectId: string;
  /** Project value not yet invoiced — offered as the amount of a new invoice. */
  suggestedAmount: number;
  orgId: string | null;
  userId: string | null;
  onClose: () => void;
}

export function InvoiceDrawer({
  open,
  invoice,
  projectId,
  suggestedAmount,
  orgId,
  userId,
  onClose,
}: Props) {
  const toast = useToast();
  const upsert = useUpsertInvoice(orgId, userId, projectId);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!open) return;
    reset(
      invoice
        ? {
            invoice_number: invoice.invoice_number,
            amount: invoice.amount,
            sent_at: invoice.sent_at ?? '',
            cancelled: invoice.status === 'cancelled',
            notes: invoice.notes ?? '',
          }
        : {
            invoice_number: '',
            amount: Number.NaN,
            sent_at: format(new Date(), 'yyyy-MM-dd'),
            cancelled: false,
            notes: '',
          },
    );
  }, [open, invoice, reset]);

  async function onSubmit(values: FormValues) {
    // 'paid' is never chosen here: the database sets it when payments cover the
    // invoice. Un-cancelling returns it to 'sent', and 0025 re-derives from there.
    const status = values.cancelled ? 'cancelled' : invoice?.status === 'paid' ? 'paid' : 'sent';
    try {
      await upsert.mutateAsync({
        id: invoice?.id ?? null,
        values: { ...values, status },
      });
      toast.success(invoice ? 'החשבונית עודכנה' : 'החשבונית נרשמה');
      onClose();
    } catch (err) {
      toast.error(`לא הצלחנו לשמור · ${errorMessage(err)}`);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="חשבונית"
      title={invoice ? `חשבונית ${invoice.invoice_number}` : 'הוסף חשבונית'}
      ariaLabel={invoice ? 'עריכת חשבונית' : 'הוסף חשבונית'}
      footer={
        <>
          <button type="button" className={styles.btnSecondary} onClick={onClose}>
            בטל
          </button>
          <button
            type="submit"
            form={FORM_ID}
            className={styles.btnPrimary}
            disabled={upsert.isPending}
          >
            שמור
          </button>
        </>
      }
    >
      <form
        id={FORM_ID}
        className={styles.form}
        noValidate
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
      >
        <p className={styles.hint}>
          רישום של חשבונית שהוצאה, למשל ב-iCount. הסטטוס "שולמה" מתעדכן לבד כשהתשלומים שנרשמו עליה
          מכסים את הסכום.
        </p>

        <div className={styles.row}>
          <div className={styles.field}>
            <label htmlFor="inv-number">מספר חשבונית</label>
            <input
              id="inv-number"
              type="text"
              dir="ltr"
              className={cn(styles.input, errors.invoice_number && styles.invalid)}
              {...register('invoice_number')}
            />
            {errors.invoice_number ? (
              <p className={styles.error}>{errors.invoice_number.message}</p>
            ) : null}
          </div>
          <div className={styles.field}>
            <label htmlFor="inv-date">תאריך הוצאה</label>
            <input
              id="inv-date"
              type="date"
              dir="ltr"
              className={styles.input}
              {...register('sent_at')}
            />
          </div>
        </div>

        <div className={styles.field}>
          <label htmlFor="inv-amount">סכום (₪)</label>
          <input
            id="inv-amount"
            type="number"
            min={0}
            step="0.01"
            dir="ltr"
            className={cn(styles.input, errors.amount && styles.invalid)}
            {...register('amount', {
              setValueAs: (v: unknown) => (v === '' || v === null ? Number.NaN : Number(v)),
            })}
          />
          {errors.amount ? <p className={styles.error}>{errors.amount.message}</p> : null}
          {!invoice && suggestedAmount > 0 ? (
            <button
              type="button"
              className={styles.fillBtn}
              onClick={() => {
                setValue('amount', suggestedAmount, { shouldValidate: true });
              }}
            >
              מלא את היתרה לחשבון: <bdi>{formatILS(suggestedAmount)}</bdi>
            </button>
          ) : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="inv-notes">הערות</label>
          <textarea
            id="inv-notes"
            rows={3}
            className={cn(styles.input, styles.textarea)}
            {...register('notes')}
          />
        </div>

        {invoice ? (
          <label className={styles.field}>
            <span>
              <input type="checkbox" {...register('cancelled')} /> החשבונית בוטלה
            </span>
            <span className={styles.hint}>
              סטטוס נוכחי: {INVOICE_STATUS_LABELS_HE[invoice.status]}. חשבונית שבוטלה לא נספרת
              בסכומים.
            </span>
          </label>
        ) : null}
      </form>
    </Drawer>
  );
}
